import { createSelector } from "reselect";
import { pathogenicitiesSortingArray } from "../../pages/variant/components/Lovd/components/constants";

const isCurrentVariant = (variant) => variant?.is_current === true;

const customSorting = (a, b, field = false) => {
  const indexA = field
    ? pathogenicitiesSortingArray.indexOf(a[field]?.toLowerCase())
    : pathogenicitiesSortingArray.indexOf(a);
  const indexB = field
    ? pathogenicitiesSortingArray.indexOf(b[field]?.toLowerCase())
    : pathogenicitiesSortingArray.indexOf(b);

  const firstElement = field ? a[field] : a;
  const secondElement = field ? b[field] : b;

  if (firstElement === undefined && secondElement === undefined) {
    return 0;
  }
  if (firstElement === undefined && secondElement !== undefined) {
    return 1;
  }
  if (firstElement !== undefined && secondElement === undefined) {
    return -1;
  }
  if (indexA !== -1 && indexB !== -1) {
    return indexA - indexB;
  }
  if (indexA !== -1) {
    return -1;
  }
  if (indexB !== -1) {
    return 1;
  }
  return 0;
};

const sortPathogenicities = (pathogenicities) => {
  /* Check if any pathogenicities array object has the property clinical_classification_curator.
    If it does, check if the value is included in the sorting array to sort the pathogenicities 
    array using this field. */
  const sortPathogenicitesByClinicalClassificationCurator =
    pathogenicities?.some(
      (obj) =>
        obj?.hasOwnProperty("clinical_classification_curator") &&
        pathogenicitiesSortingArray.includes(
          obj?.clinical_classification_curator?.toLowerCase()
        )
    );
  if (sortPathogenicitesByClinicalClassificationCurator) {
    const pathogenicitiesArr = [...pathogenicities];
    const sortedPathogenicities = pathogenicitiesArr?.sort((a, b) => {
      return customSorting(a, b, "clinical_classification_curator");
    });
    return sortedPathogenicities;
  }

  /* Check if any pathogenicities array object has the property clinical_classification_submitter.
    If it does, check if the value is included in the sorting array to sort the pathogenicities 
    array using this field. */
  const sortByClinicalClassificationSubmitter = pathogenicities?.some(
    (obj) =>
      obj?.hasOwnProperty("clinical_classification_submitter") &&
      pathogenicitiesSortingArray.includes(
        obj?.clinical_classification_submitter?.toLowerCase()
      )
  );
  if (sortByClinicalClassificationSubmitter) {
    const pathogenicitiesArr = [...pathogenicities];
    const sortedPathogenicities = pathogenicitiesArr?.sort((a, b) => {
      return customSorting(a, b, "clinical_classification_submitter");
    });
    return sortedPathogenicities;
  }
  return pathogenicities;
};

const sortByClinicalClassificationCuratorPathogenicity = (a, b) => {
  const firstElementClinicalClassificationCuratorPathogenicity = a?.variants
    ?.find(isCurrentVariant)
    ?.pathogenicity?.clinical_classification_curator?.toLowerCase();

  const secondElementClinicalClassificationCuratorPathogenicity = b?.variants
    ?.find(isCurrentVariant)
    ?.pathogenicity?.clinical_classification_curator?.toLowerCase();

  return customSorting(
    firstElementClinicalClassificationCuratorPathogenicity,
    secondElementClinicalClassificationCuratorPathogenicity
  );
};

const sortByClinicalClassificationSubmitterPathogenicity = (a, b) => {
  const aClinicalClassificationSubmitterPathogenicity = a?.variants
    ?.find(isCurrentVariant)
    ?.pathogenicity?.clinical_classification_submitter?.toLowerCase();

  const bClinicalClassificationSubmitterPathogenicity = b?.variants
    ?.find(isCurrentVariant)
    ?.pathogenicity?.clinical_classification_submitter?.toLowerCase();

  return customSorting(
    aClinicalClassificationSubmitterPathogenicity,
    bClinicalClassificationSubmitterPathogenicity
  );
};

const hasKeyClinicalClassificationCuratorPathogenicityInCurrentVariant = (
  individual
) => {
  const currentVariant = individual?.variants?.find(isCurrentVariant);
  return (
    currentVariant?.pathogenicity?.hasOwnProperty(
      "clinical_classification_curator"
    ) &&
    pathogenicitiesSortingArray.includes(
      currentVariant?.pathogenicity?.clinical_classification_curator?.toLowerCase()
    )
  );
};

const hasKeyClinicalClassificationSubmitterPathogenicityInCurrentVariant = (
  individual
) => {
  const currentVariant = individual?.variants?.find(isCurrentVariant);
  return (
    currentVariant?.pathogenicity?.hasOwnProperty(
      "clinical_classification_submitter"
    ) &&
    pathogenicitiesSortingArray.includes(
      currentVariant?.pathogenicity?.clinical_classification_submitter?.toLowerCase()
    )
  );
};

const sortIndividuals = (individuals) => {
  /* Check if any individuals array object has the property clinical_classification_curator 
    for the is_current=true variant entry.
    If it does, check if the value is included in the sorting array to use clinical_classification_curator for sorting. */
  const sortByClinicalClassificationCurator = individuals?.some((obj) =>
    hasKeyClinicalClassificationCuratorPathogenicityInCurrentVariant(obj)
  );
  if (sortByClinicalClassificationCurator) {
    const individualsArr = [...individuals];
    const sortedIndividuals = individualsArr?.sort(
      sortByClinicalClassificationCuratorPathogenicity
    );
    return sortedIndividuals;
  }

  /* Check if any individuals array object has the property clinical_classification_submitter 
    for the is_current=true variant entry.
    If it does, check if the value is included in the sorting array to use clinical_classification_submitter for sorting. */
  const sortByClinicalClassificationSubmitter = individuals?.some((obj) =>
    hasKeyClinicalClassificationSubmitterPathogenicityInCurrentVariant(obj)
  );
  if (sortByClinicalClassificationSubmitter) {
    const individualsArr = [...individuals];
    const sortedIndividuals = individualsArr?.sort(
      sortByClinicalClassificationSubmitterPathogenicity
    );
    return sortedIndividuals;
  }
  return individuals;
};

const getLovdRawData = (state) => {
  return state.variantResult && state.variantResult.lumc_lovd;
};

const getLovdData = createSelector(getLovdRawData, (items) => {
  if (items && items.length > 0) {
    return items.map((item) => {
      /* Sort the pathogenicities array */
      const pathogenicitiesArr = item?.pathogenicities;
      let sortedPathogenicities = [];
      if (pathogenicitiesArr && pathogenicitiesArr.length > 0) {
        sortedPathogenicities = sortPathogenicities(pathogenicitiesArr);
      }
      /* Sort the individuals array */
      const individualsArr = item?.individuals;
      let sortedIndividuals = [];
      if (individualsArr && individualsArr.length > 0) {
        sortedIndividuals = sortIndividuals(individualsArr);
      }
      return {
        ...item,
        pathogenicities: sortedPathogenicities,
        individuals: sortedIndividuals,
      };
    });
  }
  return null;
});

const getLOVDGeneData = (state) => {
  return __IS_COMPONENTS_PORTAL__
    ? state?.variantResult?.genes?.[state.selectedGeneIndexClinical]?.lovd_gene
    : state?.variantResult?.lovd_gene;
};

export { getLovdData, getLOVDGeneData };
